import { apiBase } from '../api/client'

export interface ClockSample {
  clientStartedMs: number
  clientFinishedMs: number
  serverUnixMs: number
}

export interface ClockEstimate {
  offsetMs: number
  roundTripMs: number
}

interface ServerTimeResponse {
  server_time_ms: number
}

const SAMPLE_COUNT = 3
const REQUEST_TIMEOUT_MS = 1500
const MAX_ABSOLUTE_OFFSET_MS = 24 * 60 * 60 * 1000

let offsetMs = 0
let clockReady = false
let calibrationInFlight: Promise<ClockEstimate | null> | null = null
const listeners = new Set<() => void>()

export function estimateServerClockOffset(sample: ClockSample): number {
  return sample.serverUnixMs - (sample.clientStartedMs + sample.clientFinishedMs) / 2
}

export function selectBestClockSample(samples: ClockSample[]): ClockEstimate | null {
  let best: ClockEstimate | null = null

  for (const sample of samples) {
    const roundTripMs = sample.clientFinishedMs - sample.clientStartedMs
    const candidateOffsetMs = estimateServerClockOffset(sample)
    if (
      !Number.isFinite(sample.clientStartedMs) ||
      !Number.isFinite(sample.clientFinishedMs) ||
      !Number.isFinite(sample.serverUnixMs) ||
      roundTripMs < 0 ||
      !Number.isFinite(candidateOffsetMs) ||
      Math.abs(candidateOffsetMs) > MAX_ABSOLUTE_OFFSET_MS
    ) {
      continue
    }
    if (best === null || roundTripMs < best.roundTripMs) {
      best = { offsetMs: candidateOffsetMs, roundTripMs }
    }
  }

  return best
}

export function getServerClockOffsetMs(): number {
  return offsetMs
}

export function isServerClockReady(): boolean {
  return clockReady
}

export function serverNowMs(clientNowMs: number = Date.now()): number {
  return clientNowMs + offsetMs
}

export function subscribeServerClock(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function calibrateServerClock(): Promise<ClockEstimate | null> {
  if (calibrationInFlight) return calibrationInFlight
  calibrationInFlight = runCalibration().finally(() => {
    calibrationInFlight = null
  })
  return calibrationInFlight
}

async function runCalibration(): Promise<ClockEstimate | null> {
  // The samples are independent, so they fire concurrently: three serial
  // round-trips used to sit on the boot network path ahead of the pair
  // seed/resolve requests. selectBestClockSample still keeps only the
  // lowest-RTT sample, so the estimate semantics are unchanged.
  const results = await Promise.all(
    Array.from({ length: SAMPLE_COUNT }, () => fetchClockSample().catch(() => null))
  )
  const samples = results.filter((sample): sample is ClockSample => sample !== null)

  const estimate = selectBestClockSample(samples)
  if (!estimate) return null
  offsetMs = estimate.offsetMs
  clockReady = true
  for (const listener of listeners) listener()
  return estimate
}

async function fetchClockSample(): Promise<ClockSample | null> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  const clientStartedMs = Date.now()
  try {
    const response = await fetch(`${apiBase}/v1/time`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    const clientFinishedMs = Date.now()
    if (!response.ok) return null
    const body = (await response.json()) as Partial<ServerTimeResponse>
    if (!Number.isFinite(body.server_time_ms)) return null
    return {
      clientStartedMs,
      clientFinishedMs,
      serverUnixMs: body.server_time_ms as number,
    }
  } finally {
    clearTimeout(timeout)
  }
}
